/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import MsDyn365, { getUrlSync } from "@msdyn365-commerce/core";
import { IHeaderViewProps, Logo } from '@msdyn365-commerce-modules/header';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IHeaderProps } from '../definition-extensions/header.ext.props.autogenerated';


/**
 * GridSizes.
 */
export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 *
 * HeaderView component.
 * @extends {React.PureComponent<IHeaderViewProps>}
 */
export class HeaderView extends React.PureComponent<IHeaderViewProps & IHeaderProps<{}>> {
    private readonly menuNode: React.RefObject<HTMLDivElement>;

    public constructor(props: IHeaderViewProps & IHeaderProps<{}>) {

        super(props);
        this.menuNode = React.createRef();
    }

    public componentDidMount(): void {
        if (this.props.config.useStickyHeader) {
            // Set header update methods to trigger on scroll
            if (MsDyn365.isBrowser) {
                window.addEventListener('scroll', () => {
                    this._configureStickyHeader();
                });
            }
            this._configureStickyHeader();
        }
    }

    public render(): JSX.Element | null {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Cannot change the prop names as props are from business logic.
        const { HeaderTag, HeaderContainer, HeaderTopBarContainer, Divider, MobileMenuContainer, MobileMenuBodyContainer, MobileMenuLinksContainer
            , config: { showQuickOrderLink, showPhoneNumber, phoneNumber }
        } = this.props;
        
        const quickOrderUrl = getUrlSync('quickorder', this.props.context.actionContext);
        return (
          <Module {...HeaderTag}>
              <Node {...HeaderContainer}>
                  <Node {...HeaderTopBarContainer}>

                      <div className='mobile-navicon-container'>
                          {this.props.navIcon}
                      </div>
                      <div className={'ms-header__desktop-logo'}>
                        {this.props.logo}
                        {showPhoneNumber == "yes" && phoneNumber ? this._renderPhoneNumber(this.props) : null}
                      </div>
                      <div className="ms-header__desktop-nav-items">
                          <div className='desktop-navicon-container' ref={this.menuNode}>
                              {this._renderReactFragment(this.props.menuBar)}
                          </div>
                          {this._renderMobileLogo()}
                          {this._renderReactFragment(this.props.search)}
                          {this.props.preferredStore}
                          {this._renderAccountBlock(this.props)}
                          <Node {...Divider} />
                          {showQuickOrderLink ? this._renderQuickViewIcon(quickOrderUrl || '#') : null}
                          {this.props.cartIcon}
                          {this._renderReactFragment(this.props.siteOptions)}
                      </div>

                  </Node>
                  <Node {...MobileMenuContainer}>
                      <Node {...MobileMenuBodyContainer}>
                          { this.props.MobileMenuHeader }
                          {this._renderReactFragment(this.props.menuBar)}
                          <Node {...MobileMenuLinksContainer}>
                              { this.props.accountLinks ? this.props.accountLinks.map(link => link) : false }
                              { this.props.siteOptions }
                              { this.props.wishListIconMobile }
                              { this.props.signInLink }
                              { this.props.signOutLink }
                          </Node>
                      </Node>
                  </Node>
              </Node>
          </Module>
        );
    }

    /**
     * Function to render phone number beside the logo.
     * @param props - IHeaderProps.
     * @returns Returns JSX.Element | null.
     */
     private _renderPhoneNumber(props: IHeaderProps<{}>): JSX.Element | null {
        const { config: { phoneNumber } } = props;
        const telephoneLink: string = `tel:+1-${phoneNumber}`;
        return (
            <div className="ms-header__phone-number">
                <a href={telephoneLink} className="ms-header__phone-number-link">{phoneNumber ? phoneNumber : ""}</a>
            </div>
        );
    }

    private _renderQuickViewIcon = (quickOrderLink: string) => (
      <div className={'ms-header__quick-order'}>
          <a href={quickOrderLink} className={'ms-header__quick-order-button'}>
              <span className={'ms-header__quick-order-button-text'}>Quick Order</span>
          </a>
      </div>
    )

    /**
     * Function to update header.
     */
    private readonly _configureStickyHeader = (): void => {
        // Get heights of cookie and promotion banners
        const defaultValue = 0;
        const headerAlertsContainer: HTMLElement | null = document.querySelector('.ms-promo-banner');
        const bannerHeights = headerAlertsContainer ? headerAlertsContainer.offsetHeight : defaultValue;

        // Triggers opacity change of header
        const headerElement = document.querySelector('header .default-container');
        if (headerElement) {
            if (MsDyn365.isBrowser && document.documentElement.scrollTop > defaultValue) {
                headerElement.classList.add('lock-opaque');
                headerElement.classList.add('fixed');
            } else {
                headerElement.classList.remove('lock-opaque');
                headerElement.classList.remove('fixed');
            }
        }

        // Update sticky header position and opacity
        const stickyHeader: HTMLElement | null = document.querySelector('.desktop-navicon-container');
        const headerLogo: HTMLElement | null = document.querySelector('.ms-header__logo');

        if (stickyHeader && headerLogo) {
            // Fix center sticky header
            const navStickyPos = headerLogo.offsetHeight + bannerHeights;
            if (MsDyn365.isBrowser && document.documentElement.scrollTop > navStickyPos) {
                stickyHeader.classList.add('fixed');
            } else {
                stickyHeader.classList.remove('fixed');
            }
        }
    };

    private _renderMobileLogo(): JSX.Element | null {
        const { config, context, typeName } = this.props;
        return (
          <Logo {
                    ...{ link: config.logoLink,
                        image: config.mobileLogoImage ?? {},
                        className: 'ms-header__mobile-logo',
                        gridSettings: context.request.gridSettings,
                        requestContext: context.request,
                        typeName }} />
        );
    }

    /**
     * Function to render account block.
     * @param props - IHeaderViewProps.
     * @returns Returns JSX.Element | null.
     */
    private readonly _renderAccountBlock = (props: IHeaderViewProps): JSX.Element | null => {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Cannot change the prop names as props are from business logic.
        const { AccountInfoDropdownParentContainer, AccountInfoDropdownPopoverConentContainer,
            accountInfoDropdownButton,
            signOutLink,
            signInLink,
            accountLinks
        } = props;
        if (AccountInfoDropdownParentContainer) {
            const accountClassName = classnames(AccountInfoDropdownParentContainer.className, 'account-desktop');
            if (AccountInfoDropdownPopoverConentContainer) {
                return (
                  <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                      {accountInfoDropdownButton}
                      <Node {...AccountInfoDropdownPopoverConentContainer}>
                          { accountLinks ? accountLinks.map((link: React.ReactNode) => link) : false }
                          {signOutLink}
                      </Node>
                  </Node>
                );
            } else if (signInLink) {
                return (
                  <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                      {signInLink}
                  </Node>
                );
            }
        }
        return null;
    };

    /**
     * Function to render react fragment.
     * @param items - React nodes.
     * @returns Returns JSX.Element | null.
     */
    private readonly _renderReactFragment = (items: React.ReactNode[]): JSX.Element | null => {
        return (
          <>
              {ArrayExtensions.hasElements(items) ? items.map((slot: React.ReactNode) => {
                  return (<>
                      {slot}
                  </>);
              }) : null}
          </>
        );
    };
}

export default HeaderView;
